import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';

import FleetMapDetail from 'components/FleetMapDetail';
import { type TImportedTurbineMappedField } from 'horizon/components/Assets/CreateAssetsModal/types';
import {
  type AtlasGqlFilterBy,
  type AtlasGqlTurbine,
  useGetFleetMapDataQuery,
} from 'types/atlas-graphql';
import { MARKER_BLUE, TURBINE_ICON } from 'utils/constants';

import { getTurbineInspectionData } from './helpers';
import { TurbinePointMap } from './TurbinePointMap';
import { type TTurbineProps } from './types';

type FleetMapAsset = AtlasGqlTurbine & {
  rowIndex?: number;
  assetTypeName?: string;
};

const hasPosition = (x: FleetMapAsset) => x.latLng && x.latLng.length === 2;

const getTurbineProps = (
  turbine: FleetMapAsset,
  previewMode: boolean,
  isWorkOrderTasksList: boolean
): TTurbineProps => {
  const [lat, lng] = turbine.latLng as number[];
  const inspectionData = getTurbineInspectionData(turbine.inspections);

  return {
    key: turbine.id,
    id: turbine.id,
    position: { lat, lng },
    content: (
      <FleetMapDetail turbine={turbine} previewMode={previewMode} inspectionData={inspectionData} />
    ),
    name: turbine.name ?? undefined,
    assetTypeName: turbine.assetTypeName,
    rowIndex: turbine.rowIndex,
    location: turbine.location
      ? {
          id: turbine.location.id,
          name: turbine.location.name,
        }
      : undefined,
    ...(isWorkOrderTasksList
      ? {
          isLinkedWithTask: turbine.isLinkedWithTask,
          isTasksCompleted: turbine.isTasksCompleted,
        }
      : { inspectionData }),
  };
};

/**
 * @property parentId - optional - parent asset (site) uniqe id. Do not use
 *                                  if getting entire fleet map for a given
 *                                  customer or inserting static turbine data.
 * @satelliteView: - optional    - if true, map displays with satellite view
 * @importedTurbine: - optional  - an array of turbines to display on the map
 *                                  instead of requesting turbine data from server
 * @previewMode: - optional      - if true, apply functional and styling changes such
 *                                  as increasing map height
 * @setTurbineToUpdate - optional - if preview mode is true, also send this setter
 *                                  to enable real-time location updates
 *                                  when dragging and dropping pins
 */
type FleetMapProps = {
  parentId?: string;
  satelliteView?: boolean;
  importedTurbines?: Array<FleetMapAsset>;
  previewMode?: boolean;
  setTurbineToUpdate?: Dispatch<SetStateAction<TImportedTurbineMappedField | undefined>>;
  isWorkOrderTasksList?: boolean;
  /** When false, map is keep-alive hidden; refresh size when shown again. */
  isActive?: boolean;
};

export const FleetMap = ({
  parentId,
  satelliteView,
  importedTurbines,
  previewMode = false,
  setTurbineToUpdate,
  isWorkOrderTasksList = false,
  isActive,
}: FleetMapProps) => {
  const [turbines, setTurbines] = useState<Array<FleetMapAsset>>(importedTurbines || []);
  useEffect(() => {
    if (importedTurbines) {
      // force update when imported turbines changes to persist changes from
      // map preview drag and drop
      setTurbines(importedTurbines);
    }
  }, [importedTurbines]);

  const contentHeight = previewMode ? '70vh' : '35rem';

  /*
    Leaflet reapplies a Marker's position, icon and content whenever they change identity,
    so rebuilding this list on every render re-clusters every Turbine on the map.
  */
  const filteredTurbines = useMemo(
    () =>
      turbines
        .filter(hasPosition)
        .map(turbine => getTurbineProps(turbine, previewMode, isWorkOrderTasksList)),
    [turbines, previewMode, isWorkOrderTasksList]
  );
  const filters: AtlasGqlFilterBy[] = useMemo(
    () =>
      parentId
        ? [
            {
              key: 'siteId',
              values: [parentId],
            },
          ]
        : [],

    [parentId]
  );

  const { data: fleetMapData, loading } = useGetFleetMapDataQuery({
    variables: {
      filterBy: filters,
      limit: 1,
    },
    skip: !!importedTurbines,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (fleetMapData?.turbines) {
      setTurbines(fleetMapData.turbines);
    }
  }, [fleetMapData]);

  return (
    // @ts-ignore AN child component is not TS yet
    <TurbinePointMap
      loading={loading}
      items={filteredTurbines}
      icon={previewMode ? MARKER_BLUE : TURBINE_ICON}
      height={contentHeight}
      mapHeight={contentHeight}
      sideList={previewMode ? true : false}
      satelliteView={satelliteView}
      enableClustering
      setTurbineToUpdate={setTurbineToUpdate}
      isDraggable={previewMode ? true : false}
      isWorkOrderTasksList={isWorkOrderTasksList}
      isActive={isActive}
    />
  );
};
